import type { CellComponent, RowComponent } from 'tabulator-tables';
import { TabulatorFull as Tabulator } from 'tabulator-tables';

import {
    datesSorter,
    icon,
    linkFormatter,
    populateSelect,
    readData,
    toOptions,
    uniqueValues,
    wireFilters,
} from '../tabulator';

function dateFormatter(cell: CellComponent): string {
    const date = new Date(cell.getValue() as string);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function reviewedFormatter(cell: CellComponent): string {
    return (cell.getValue() as unknown[]).length > 0 ? icon('circle-check') : '';
}

interface Metric {
    name: string;
    formatted_value: string;
}

interface ReviewQuestion {
    question: string;
    answer: string;
}

interface Review {
    author: string;
    submitted: string;
    summary: ReviewQuestion[];
}

interface SubmissionRow {
    id: number;
    metrics: Metric[];
    reviews: Review[];
    [key: string]: unknown;
}

interface ReviewRow {
    id: number;
    summary: ReviewQuestion[];
    [key: string]: unknown;
}

function drawMetrics(metrics: Metric[]): string {
    let html =
        '<div class="w-1/2 sm:w-full md:w-1/2"><div class="grid grid-cols-1 sm:grid-cols-12 gap-x-2">';
    metrics.forEach((metric) => {
        html += `<dt class="sm:col-span-4 lg:col-span-3">${metric.name}</dt>`;
        html += `<dd class="sm:col-span-8 lg:col-span-9">${metric.formatted_value}</dd>`;
    });
    return html + '</div></div>';
}

function drawReviews(reviews: Review[]): string {
    if (reviews.length === 0) return '';
    let html =
        '<div class="w-full"><div class="flex flex-col"><dt class="w-full">Reviews</dt><dd class="w-full">';
    reviews.forEach((review) => {
        html += `<div class="bg-white border border-gray-200 rounded-md shadow-sm mt-1"><div class="p-3">`;
        html += `<p class="mb-0"><span class="font-bold">${review.author}</span> - ${new Date(review.submitted).toLocaleDateString()}</p>`;
        html += '<dl class="grid grid-cols-12 gap-x-2 mt-4 mb-0">';
        review.summary.forEach((q) => {
            html += `<dt class="col-span-4">${q.question}</dt><dd class="col-span-8">${q.answer}</dd>`;
        });
        html += '</dl></div></div>';
    });
    return html + '</dd></div></div>';
}

function detailFormatter(data: SubmissionRow): string {
    return drawMetrics(data.metrics) + drawReviews(data.reviews);
}

function reviewDetailFormatter(data: ReviewRow): string {
    let html = '<dl class="grid grid-cols-12 gap-x-2 mt-4 mb-0"><div class="col-span-12">';
    data.summary.forEach((q) => {
        html += `<dt class="col-span-4">${q.question}</dt><dd class="col-span-8">${q.answer}</dd>`;
    });
    return html + '</div></dl>';
}

function groupByMonth(
    data: Record<string, unknown>[],
    field: string,
): { value: string; label: string }[] {
    const dates = data.map(
        (row) =>
            new Date(
                field
                    .split('.')
                    .reduce(
                        (o: unknown, key) => (o as Record<string, unknown>)[key],
                        row,
                    ) as string,
            ),
    );
    const counted = dates.reduce<Record<string, { name: string; count: number }>>((acc, date) => {
        const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
        const mo = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        const key = `${ye}-${mo}`;
        if (key in acc) {
            acc[key].count++;
        } else {
            acc[key] = {
                name: date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
                count: 1,
            };
        }
        return acc;
    }, {});
    return Object.keys(counted)
        .sort()
        .map((key) => ({ value: key, label: `${counted[key].name} (${counted[key].count})` }));
}

function createDetailToggle<T extends { id: number }>(formatter: (data: T) => string) {
    const expandedIds = new Set<number>();

    function toggle(row: RowComponent): void {
        const id = (row.getData() as T).id;
        if (expandedIds.has(id)) {
            expandedIds.delete(id);
        } else {
            expandedIds.add(id);
        }
        row.reformat();
    }

    function toggleColumnFormatter(cell: CellComponent): string {
        return expandedIds.has((cell.getRow().getData() as T).id)
            ? icon('chevron-down')
            : icon('chevron-right');
    }

    function rowFormatter(row: RowComponent): void {
        const data = row.getData() as T;
        const element = row.getElement();
        let holder = element.querySelector<HTMLElement>('.row-detail');
        if (expandedIds.has(data.id)) {
            if (!holder) {
                holder = document.createElement('div');
                holder.className = 'row-detail p-3 bg-gray-50 border-t border-gray-200';
                element.appendChild(holder);
            }
            holder.innerHTML = formatter(data);
        } else {
            holder?.remove();
        }
    }

    return { toggle, toggleColumnFormatter, rowFormatter };
}

function monthPredicate(filterValue: string, rowValue: unknown): boolean {
    const date = new Date(rowValue as string);
    const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
    const mo = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
    return `${ye}-${mo}` === filterValue;
}

function initSubmissionsTable(el: HTMLElement): void {
    const src = el.dataset.src!;
    const data = readData<SubmissionRow>(src);
    const detail = createDetailToggle(detailFormatter);
    const table = new Tabulator(el, {
        data,
        index: 'id',
        pagination: true,
        paginationSize: 10,
        layout: 'fitColumns',
        initialSort: [{ column: 'submitted', dir: 'desc' }],
        rowFormatter: detail.rowFormatter,
        columns: [
            {
                title: '',
                formatter: detail.toggleColumnFormatter,
                width: 40,
                hozAlign: 'center',
                headerSort: false,
                cellClick: (_e, cell) => detail.toggle(cell.getRow()),
            },
            { title: 'Title', field: 'title' },
            { title: 'Site', field: 'site' },
            {
                title: 'Submitted',
                field: 'submitted',
                sorter: datesSorter,
                formatter: dateFormatter,
            },
            {
                title: 'Reviewed',
                field: 'reviews',
                formatter: reviewedFormatter,
                hozAlign: 'center',
                headerSort: false,
            },
            { title: '', field: 'url', formatter: linkFormatter, headerSort: false },
        ],
    });

    const titleFilter = document.getElementById(`filter-title-${src}`) as HTMLInputElement;
    const siteSelect = document.getElementById(`filter-site-${src}`) as HTMLSelectElement;
    const submittedSelect = document.getElementById(`filter-submitted-${src}`) as HTMLSelectElement;
    const reviewsSelect = document.getElementById(`filter-reviews-${src}`) as HTMLSelectElement;

    populateSelect(siteSelect, toOptions(uniqueValues(data, 'site')));
    populateSelect(submittedSelect, groupByMonth(data, 'submitted'));
    populateSelect(reviewsSelect, [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
    ]);

    wireFilters(table, [
        {
            select: titleFilter,
            field: 'title',
            predicate: (fv, rv) => String(rv).toLowerCase().includes(fv.toLowerCase()),
        },
        { select: siteSelect, field: 'site' },
        { select: submittedSelect, field: 'submitted', predicate: monthPredicate },
        {
            select: reviewsSelect,
            field: 'reviews',
            predicate: (fv, rv) => (rv as unknown[]).length > 0 === (fv === 'true'),
        },
    ]);
}

function initReviewsTable(el: HTMLElement): void {
    const src = el.dataset.src!;
    const data = readData<ReviewRow>(src);
    const detail = createDetailToggle(reviewDetailFormatter);
    const table = new Tabulator(el, {
        data,
        index: 'id',
        pagination: true,
        paginationSize: 10,
        layout: 'fitColumns',
        initialSort: [{ column: 'submitted', dir: 'desc' }],
        rowFormatter: detail.rowFormatter,
        columns: [
            {
                title: '',
                formatter: detail.toggleColumnFormatter,
                width: 40,
                hozAlign: 'center',
                headerSort: false,
                cellClick: (_e, cell) => detail.toggle(cell.getRow()),
            },
            { title: 'Title', field: 'submission.title' },
            { title: 'Site', field: 'submission.site' },
            { title: 'Requestor', field: 'submission.author' },
            {
                title: 'Reviewed',
                field: 'submitted',
                sorter: datesSorter,
                formatter: dateFormatter,
            },
            { title: '', field: 'submission.url', formatter: linkFormatter, headerSort: false },
        ],
    });

    const titleFilter = document.getElementById(`filter-title-${src}`) as HTMLInputElement;
    const siteSelect = document.getElementById(`filter-site-${src}`) as HTMLSelectElement;
    const submittedSelect = document.getElementById(`filter-submitted-${src}`) as HTMLSelectElement;

    populateSelect(siteSelect, toOptions(uniqueValues(data, 'submission.site')));
    populateSelect(submittedSelect, groupByMonth(data, 'submitted'));

    wireFilters(table, [
        {
            select: titleFilter,
            field: 'submission.title',
            predicate: (fv, rv) => String(rv).toLowerCase().includes(fv.toLowerCase()),
        },
        { select: siteSelect, field: 'submission.site' },
        { select: submittedSelect, field: 'submitted', predicate: monthPredicate },
    ]);
}

window.addEventListener('load', () => {
    document
        .querySelectorAll<HTMLElement>('[data-table-type="portfolio-submission"]')
        .forEach(initSubmissionsTable);
    document
        .querySelectorAll<HTMLElement>('[data-table-type="portfolio-review"]')
        .forEach(initReviewsTable);
});
